import { useEffect } from "react";

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  ogUrl?: string;
  twitterCard?: string;
  twitterImage?: string;
  noindex?: boolean;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
}

/**
 * SEO component — updates document head meta tags dynamically.
 * Call this on every page that needs unique SEO meta tags.
 */
export const SEO = ({
  title,
  description = "KCANIA — Nền tảng kết nối doanh nghiệp công nghệ Carbon & Nano Hàn Quốc với thị trường Việt Nam. Tìm kiếm sản phẩm, hợp tác kỹ thuật và kết nối B2B.",
  keywords = "KCANIA, sản phẩm Hàn Quốc, công nghệ Carbon Nano, B2B Việt Nam, doanh nghiệp Hàn Quốc, hợp tác kỹ thuật, nhập khẩu công nghệ",
  canonical,
  ogTitle,
  ogDescription,
  ogImage = "https://static.readdy.ai/image/302c4174898c881693fe2d12476be64a/fcb736df9a5a33bf7d4c294e6dc25119.png",
  ogType = "website",
  ogUrl,
  twitterCard = "summary_large_image",
  twitterImage,
  noindex = false,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags,
}: SEOProps) => {
  useEffect(() => {
    const baseTitle = "KCANIA — Kết Nối Doanh Nghiệp Công Nghệ Hàn–Việt";
    const fullTitle = title ? `${title} | ${baseTitle}` : baseTitle;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    // Canonical
    let linkCanonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    const canonicalUrl = canonical || window.location.href.split("?")[0];
    linkCanonical.setAttribute("href", canonicalUrl);

    // Open Graph
    setMeta("og:title", ogTitle || fullTitle, "property");
    setMeta("og:description", ogDescription || description, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:url", ogUrl || canonicalUrl, "property");
    setMeta("og:locale", "vi_VN", "property");
    setMeta("og:site_name", "KCANIA", "property");

    // Twitter
    setMeta("twitter:card", twitterCard);
    setMeta("twitter:title", ogTitle || fullTitle);
    setMeta("twitter:description", ogDescription || description);
    setMeta("twitter:image", twitterImage || ogImage);

    // Robots
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");

    // Article meta
    if (articlePublishedTime) setMeta("article:published_time", articlePublishedTime, "property");
    if (articleModifiedTime) setMeta("article:modified_time", articleModifiedTime, "property");
    if (articleAuthor) setMeta("article:author", articleAuthor, "property");
    if (articleSection) setMeta("article:section", articleSection, "property");
    if (articleTags) {
      // Remove old article tags first
      document.querySelectorAll("meta[property='article:tag']").forEach((el) => el.remove());
      articleTags.forEach((tag) => {
        const el = document.createElement("meta");
        el.setAttribute("property", "article:tag");
        el.setAttribute("content", tag);
        document.head.appendChild(el);
      });
    }

    // Last-modified
    let lastMod = document.querySelector("meta[http-equiv='last-modified']");
    if (!lastMod) {
      lastMod = document.createElement("meta");
      lastMod.setAttribute("http-equiv", "last-modified");
      document.head.appendChild(lastMod);
    }
    lastMod.setAttribute("content", new Date().toUTCString());

    return () => {
      // Clean up article tags on unmount
      document.querySelectorAll("meta[property='article:tag']").forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    ogUrl,
    twitterCard,
    twitterImage,
    noindex,
    articlePublishedTime,
    articleModifiedTime,
    articleAuthor,
    articleSection,
    articleTags,
  ]);

  return null;
};

export default SEO;